import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Flex, Text } from '@chakra-ui/core';
import { bindActionCreators } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';
import { PageDefault } from '../common/components/layouts/Page/PageDefault';
import { UserForm } from '../common/components/UserForm/UserForm';
import { operatorsActions } from '../redux/operators/actions';
import Can from '../roleBasedAccess/Can';
import { PrimaryButton } from '../common/components/Buttons';
import { usersAPI } from '../common/api/users';
import { toastActions } from '../redux/toast/actions';
import { TOAST_MESSAGE } from '../common/components/Toasts/Toast';

const invitationLabels = {
  pending: 'À envoyer',
  processing: 'En cours d’envoi',
  sent: 'Envoyée',
  failed: 'Échec de l’envoi',
  canceled: 'Annulée',
};

const OperatorEdit = ({
  match: {
    params: { operatorId },
  },
  operator,
  error,
  pending,
  deletePending,
  getOperator,
  updateOperator,
  deleteOperator,
}) => {
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.authentication);
  const [invitationPending, setInvitationPending] = useState(false);

  useEffect(() => {
    getOperator(operatorId);
  }, [operatorId, getOperator]);

  const onSubmit = updatedOperatorValues => {
    updateOperator(operatorId, updatedOperatorValues);
  };

  const onDeleteOperator = () => {
    // eslint-disable-next-line no-alert
    const confirm = window.confirm(
      `Voulez-vous supprimer l'opérateur n°${operator.id} - ${operator.firstName} ${operator.lastName} ?`,
    );
    if (confirm) deleteOperator(operatorId);
  };

  const onResendInvitation = () => {
    setInvitationPending(true);
    usersAPI
      .resendInvitation(operatorId)
      .then(() => getOperator(operatorId))
      .then(() => dispatch(toastActions.success(TOAST_MESSAGE.success.invitationResend)))
      .catch(() => dispatch(toastActions.error(TOAST_MESSAGE.error.invitationResend)))
      .finally(() => setInvitationPending(false));
  };

  const isHbReferent =
    operator?.role === 'référent entreprise' &&
    operator?.companies?.some(company => company.source === 'HB');
  const invitationState = operator?.hbInvitationState;
  const invitationIsUnavailable = operator?.hbInvitationResendAvailable === false;
  const invitationStateLabel =
    invitationState === 'processing' && !invitationIsUnavailable
      ? 'Envoi interrompu'
      : invitationLabels[invitationState] || 'Non envoyée';
  const invitationIsResend = ['processing', 'sent', 'failed', 'canceled'].includes(invitationState);

  const deleteAction = (
    <Can
      perform="delete"
      yes={() => (
        <PrimaryButton
          isLoading={deletePending}
          loadingText="Suppression..."
          onClick={onDeleteOperator}
        >
          Supprimer
        </PrimaryButton>
      )}
    />
  );

  const invitationAction = (
    <Can
      perform="users:manage_invitations"
      yes={() => (
        <Flex alignItems="center" gridGap={2}>
          <Text fontSize="sm" color="semiDarkGray.600">
            Invitation : {invitationStateLabel}
          </Text>
          <PrimaryButton
            isLoading={invitationPending}
            isDisabled={invitationIsUnavailable}
            loadingText="Programmation..."
            onClick={onResendInvitation}
          >
            {invitationIsResend ? 'Renvoyer l’invitation' : 'Envoyer l’invitation'}
          </PrimaryButton>
        </Flex>
      )}
    />
  );

  return (
    <PageDefault
      title={
        user.role === 'coordinateur entreprises'
          ? 'Édition du référent entreprise'
          : 'édition de la fiche opérateur'
      }
      pageAction={
        isHbReferent ? (
          <Flex alignItems="center" gridGap={3} flexWrap="wrap">
            {invitationAction}
            {deleteAction}
          </Flex>
        ) : (
          deleteAction
        )
      }
    >
      <UserForm
        initialValues={operator}
        pending={pending}
        onSubmit={onSubmit}
        error={error}
        mode="edit"
      />
    </PageDefault>
  );
};

const mapStateToProps = ({ operators: state }) => ({
  error: state.error,
  pending: state.pending,
  deletePending: state.deletePending,
  operator: state.operator,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      getOperator: operatorsActions.getOperator,
      updateOperator: operatorsActions.updateOperator,
      deleteOperator: operatorsActions.deleteOperator,
    },
    dispatch,
  );

OperatorEdit.propTypes = {
  pending: PropTypes.bool,
  deletePending: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  error: PropTypes.object,
  getOperator: PropTypes.func.isRequired,
  updateOperator: PropTypes.func.isRequired,
  deleteOperator: PropTypes.func.isRequired,
};

OperatorEdit.defaultProps = {
  pending: true,
  deletePending: false,
  error: {},
};

export default connect(mapStateToProps, mapDispatchToProps)(OperatorEdit);
